/**
 * Utility functions for the company stage strategy
 */

import { runAIMatches, runMultiMatches, fetchQueries } from '../../api';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../../discoveryQueueSchema';

// Clean confirmation message for session restoration
export const restore_message = "I reviewed your existing workforce, and this is the distribution across different company stages. Which company stage(s) would you like to target?";

// Company stage hex values for API integration
export const COMPANY_STAGE_HEX_VALUES = {
  CURRENT_EARLY: '5ea7d6a70509490007fb5106',
  CURRENT_MID: '5ea7d7040509490007fb510a', 
  CURRENT_LATE: '5ea7d7730509490007fb510e',
  CURRENT_PUBLIC: '5eb3ba57d4d733000746ecfc',
  RECENT_EARLY: '5ea7d6a70509490007fb5106',
  RECENT_MID: '5ea7d7040509490007fb510a',
  RECENT_LATE: '5ea7d7730509490007fb510e', 
  RECENT_PUBLIC: '5eb3ba57d4d733000746ecfc',
  PAST_EARLY: '5ea7d6a70509490007fb5106',
  PAST_MID: '5ea7d7040509490007fb510a',
  PAST_LATE: '5ea7d7730509490007fb510e',
  PAST_PUBLIC: '5eb3ba57d4d733000746ecfc'
};

/**
 * Generate company stage data and populate centralized queue
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Current decision object
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Object>} The company stage data
 */
export const generateCompanyStageData = async (messages, setMessages, decision, useCache = true) => {
  // Detect if this is a fresh start (start from scratch or start with JD)
  const isFreshStart = !decision.getAllRequirements || 
    (decision.getAllRequirements && (!decision.getAllRequirements() || decision.getAllRequirements().length === 0));
  
  console.log('🎯 COMPANY STAGE: Fresh start detected:', isFreshStart);
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating company stage data and populating centralized queue...`
            }
          : msg
      );
      return updatedMessages;
    });
  }

  try {
    console.log('🎯 COMPANY STAGE: Starting data generation and queue population');

    // Fetch queries dictionary
    const queries = await fetchQueries();
    if (!queries || Object.keys(queries).length === 0) {
      throw new Error('Failed to fetch queries dictionary');
    }

    console.log('🎯 COMPANY STAGE: Fetched queries:', Object.keys(queries).length);

    // Group queries by ui_categories with deduplication
    const currentStageQueries = [];
    const pastStageQueries = [];
    const recentStageQueries = [];
    const processedQueryIds = new Set(); // Track processed queries to avoid duplicates

    Object.keys(queries).forEach((queryId) => {
      let queryData = queries[queryId];
      queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
      
      if (queryData.macro_taxonomy.length === 0 || processedQueryIds.has(queryId)) {
        return;
      }
      
      if (queryData.macro_taxonomy[0] && typeof queryData.macro_taxonomy[0] === 'string' && queryData.macro_taxonomy[0].indexOf("Stage") >= 0) {
        const macroTax = queryData.macro_taxonomy[0].toLowerCase();
        
        // More precise filtering with priority order: Current > Recent > Past
        if (macroTax.includes("current") && !macroTax.includes("past or current")) {
          currentStageQueries.push({ id: queryId, name: queryData.name });
          processedQueryIds.add(queryId);
          console.log(`🎯 COMPANY STAGE: Added to CURRENT: ${queryData.name} (${queryId})`);
        } else if (macroTax.includes("recent") && !macroTax.includes("current")) {
          recentStageQueries.push({ id: queryId, name: queryData.name });
          processedQueryIds.add(queryId);
          console.log(`🎯 COMPANY STAGE: Added to RECENT: ${queryData.name} (${queryId})`);
        } else if (macroTax.includes("past") && !macroTax.includes("current") && !macroTax.includes("recent")) {
          pastStageQueries.push({ id: queryId, name: queryData.name });
          processedQueryIds.add(queryId);
          console.log(`🎯 COMPANY STAGE: Added to PAST: ${queryData.name} (${queryId})`);
        } else {
          console.log(`🎯 COMPANY STAGE: Skipped ambiguous query: ${queryData.name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
        }
      }
    });

    console.log('🎯 COMPANY STAGE: Grouped queries:', {
      current: currentStageQueries.length,
      past: pastStageQueries.length,
      recent: recentStageQueries.length
    });

    // Create queue items for each category
    const queueItems = [];

    // 1. Current Stage queue item
    if (currentStageQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      const currentBoolStr = isFreshStart ? '' : `((${currentStageQueries.map(q => q.id).join(' OR ')}))`;
      
      const currentQueueItem = {
        queue_id: `current_stage_${Date.now()}`,
        section_id: 'current_stage',
        section_name: 'Current Company Stage',
        section_type: 'Must Have',
        section_order: 1,
        topic: 'company_stage',
        bool_str: currentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What current company stages would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different current company stages. Which current company stage(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Current Company Stages',
          chart_subtitle: isFreshStart 
            ? 'Select the current company stages you want to target'
            : 'Employees who currently work at these company stages',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true // For fresh starts, show all options
        },
        template_props: {
          data: currentStageQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(currentQueueItem);
    }

    // 2. Past Stage queue item
    if (pastStageQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      const pastBoolStr = isFreshStart ? '' : `((${pastStageQueries.map(q => q.id).join(' OR ')}))`;
      
      const pastQueueItem = {
        queue_id: `past_stage_${Date.now() + 1}`,
        section_id: 'past_stage',
        section_name: 'Past Company Stage',
        section_type: 'Must Have',
        section_order: 2,
        topic: 'company_stage',
        bool_str: pastBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What past company stages would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different past company stages. Which past company stage(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Past Company Stages',
          chart_subtitle: isFreshStart 
            ? 'Select the past company stages you want to target'
            : 'Employees who previously worked at these company stages',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true // For fresh starts, show all options
        },
        template_props: {
          data: pastStageQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(pastQueueItem);
    }

    // 3. Recent Stage queue item
    if (recentStageQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      const recentBoolStr = isFreshStart ? '' : `((${recentStageQueries.map(q => q.id).join(' OR ')}))`;
      
      const recentQueueItem = {
        queue_id: `recent_stage_${Date.now() + 2}`,
        section_id: 'recent_stage',
        section_name: 'Recent Company Stage',
        section_type: 'Must Have',
        section_order: 3,
        topic: 'company_stage',
        bool_str: recentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What recent company stages would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different recent company stages. Which recent company stage(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Recent Company Stages',
          chart_subtitle: isFreshStart 
            ? 'Select the recent company stages you want to target'
            : 'Employees who recently worked at these company stages',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true // For fresh starts, show all options
        },
        template_props: {
          data: recentStageQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(recentQueueItem);
    }

    // Add all queue items to centralized queue
    console.log('🎯 COMPANY STAGE: Adding', queueItems.length, 'items to centralized queue');
    
    // 🎯 CRITICAL FIX: Check if central discovery queue is properly initialized
    if (!centralDiscoveryQueue.sessionId) {
      console.error('❌ COMPANY STAGE: Central discovery queue not initialized - no session ID');
      throw new Error('Central discovery queue not initialized - no session ID');
    }
    
    console.log('✅ COMPANY STAGE: Central discovery queue is initialized with session ID:', centralDiscoveryQueue.sessionId);
    
    for (const queueItem of queueItems) {
      try {
        const addedItem = await centralDiscoveryQueue.addQueueItem(queueItem);
        
        // Calculate items count based on template type
        let itemsCount = 0;
        if (queueItem.template_type === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
          itemsCount = queueItem.template_props.data ? queueItem.template_props.data.length : 0;
        } else if (queueItem.template_props.sections && queueItem.template_props.sections[0]) {
          itemsCount = queueItem.template_props.sections[0].items ? queueItem.template_props.sections[0].items.length : 0;
        }
        
        console.log('✅ COMPANY STAGE: Added queue item:', {
          section_id: addedItem.section_id,
          section_name: addedItem.section_name,
          template_type: queueItem.template_type,
          items_count: itemsCount
        });
      } catch (error) {
        console.error('❌ COMPANY STAGE: Error adding queue item:', queueItem.section_id, error);
      }
    }

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nSuccessfully populated ${queueItems.length} company stage queue items.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    console.log('✅ COMPANY STAGE: Successfully populated centralized queue with', queueItems.length, 'items');

    // Return success data for backward compatibility
    return {
      stageData: {
        totalItems: queueItems.length,
        currentStage: currentStageQueries.length,
        pastStage: pastStageQueries.length,
        recentStage: recentStageQueries.length
      },
      totalCount: currentStageQueries.length + pastStageQueries.length + recentStageQueries.length,
      aiMatchesResult: {
        companyStageData: {
          queueItemsCreated: queueItems.length,
          categories: ['current_stage', 'past_stage', 'recent_stage']
        },
        companyStageTotal: queueItems.length
      }
    };

  } catch (error) {
    console.error('❌ COMPANY STAGE: Error generating company stage data:', error);
    
    // Update thinking accordion with error
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nError generating company stage data: ${error.message}`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    throw error;
  }
};
